import { useCallback, useEffect, useState } from "react";

import {
	clearOverrideValue,
	type KeybindingOverrideMap,
	keybindingOverridesChangeEvent,
	keybindingOverridesVersion,
	readKeybindingOverrides,
	setOverrideValue,
	writeKeybindingOverrides
} from "~/helpers/keybindingOverrides";

export interface UseKeybindingOverridesReturn {
	disableAction: (actionId: string) => void;
	overrides: KeybindingOverrideMap;
	resetAction: (actionId: string) => void;
	resetAll: () => void;
	setBinding: (actionId: string, binding: string) => void;
}

const persist = (overrides: KeybindingOverrideMap) => {
	writeKeybindingOverrides({ overrides, version: keybindingOverridesVersion });
	window.dispatchEvent(new Event(keybindingOverridesChangeEvent));
};

const useKeybindingOverrides = (): UseKeybindingOverridesReturn => {
	const [overrides, setOverrides] = useState(() => readKeybindingOverrides().overrides);

	useEffect(() => {
		const sync = () => setOverrides(readKeybindingOverrides().overrides);
		window.addEventListener(keybindingOverridesChangeEvent, sync);
		window.addEventListener("storage", sync);
		return () => {
			window.removeEventListener(keybindingOverridesChangeEvent, sync);
			window.removeEventListener("storage", sync);
		};
	}, []);

	const setBinding = useCallback((actionId: string, binding: string) => {
		persist(setOverrideValue(readKeybindingOverrides().overrides, actionId, binding));
	}, []);

	const disableAction = useCallback((actionId: string) => {
		persist(setOverrideValue(readKeybindingOverrides().overrides, actionId, null));
	}, []);

	const resetAction = useCallback((actionId: string) => {
		persist(clearOverrideValue(readKeybindingOverrides().overrides, actionId));
	}, []);

	const resetAll = useCallback(() => {
		persist({});
	}, []);

	return { disableAction, overrides, resetAction, resetAll, setBinding };
};

export default useKeybindingOverrides;
